/**
 * Magic Link Verification Page
 * Handles passwordless login via magic link
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Mail, Wand2 } from 'lucide-react';
import { authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import vizzexLogo from '../../assets/VIZZEX.250.png';

type PageMode = 'verify' | 'request';
type VerifyStatus = 'loading' | 'success' | 'error';

export default function MagicLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  const token = searchParams.get('token');
  const mode: PageMode = token ? 'verify' : 'request';

  // Verify mode state
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>('loading');
  const [verifyMessage, setVerifyMessage] = useState('');

  // Request mode state
  const [email, setEmail] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState('');

  // Handle token verification
  useEffect(() => {
    if (mode === 'verify' && token) {
      const verifyToken = async () => {
        try {
          setVerifyStatus('loading');
          const response = await authApi.verifyMagicLink(token);

          if (response.success) {
            setVerifyStatus('success');
            setVerifyMessage('Login successful!');
            await refreshUser();
            
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 1500);
          } else {
            throw new Error('Verification failed');
          }
        } catch (err) {
          console.error('Magic link verification error:', err);
          const error = err as { response?: { data?: { error?: string } }; message?: string };
          setVerifyStatus('error');
          setVerifyMessage(error.response?.data?.error || error.message || 'Invalid or expired magic link');
        }
      };

      verifyToken();
    }
  }, [mode, token, navigate, refreshUser]);

  // Handle magic link request
  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestError('');
    setRequestLoading(true);

    try {
      const response = await authApi.requestMagicLink(email);
      
      if (response.success) {
        setRequestSent(true);
      } else {
        throw new Error('Failed to send magic link');
      }
    } catch (err) {
      console.error('Magic link request error:', err);
      setRequestError('Failed to send magic link. Please try again.');
    } finally {
      setRequestLoading(false);
    }
  };

  // Verification mode UI
  if (mode === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md animate-slide-up">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={vizzexLogo} 
              alt="VizzEx Pro" 
              className="w-[250px] mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          </div>

          {/* Status Card */}
          <div className="glass rounded-2xl p-8 text-center">
            {verifyStatus === 'loading' && (
              <>
                <div className="w-16 h-16 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-[var(--color-primary-400)] animate-spin" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Verifying...</h2>
                <p className="text-[var(--text-secondary)]">Please wait while we verify your magic link.</p>
              </>
            )}

            {verifyStatus === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-[var(--color-success)]" />
                </div>
                <h2 className="text-xl font-semibold mb-2">{verifyMessage}</h2>
                <p className="text-[var(--text-secondary)]">You're now logged in.</p>
                <p className="text-sm text-white/70 mt-4">Redirecting to dashboard...</p>
              </>
            )}

            {verifyStatus === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-[var(--color-error)]/20 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-[var(--color-error)]" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Verification Failed</h2>
                <p className="text-[var(--text-secondary)] mb-6">{verifyMessage}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/login"
                    className="px-6 py-3 bg-[var(--bg-tertiary)] text-white font-medium rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors text-center"
                  >
                    Back to Login
                  </Link>
                  <Link
                    to="/auth/magic-link"
                    className="px-6 py-3 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-center"
                  >
                    Request New Link
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Request mode UI
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--color-primary-400)] to-[var(--color-accent-500)] flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">V</span>
          </div>
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          <p className="text-[var(--text-secondary)] mt-2">Sign in with a magic link</p>
        </div>

        <div className="glass rounded-2xl p-8">
          {requestSent ? (
            // Success state
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[var(--color-success)]" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Check your email</h2>
              <p className="text-[var(--text-secondary)] mb-6">
                We've sent a magic link to <strong>{email}</strong>. 
                Click the link in the email to sign in.
              </p>
              <p className="text-sm text-white/70 mb-4">
                The link will expire in 15 minutes.
              </p>
              <button
                onClick={() => {
                  setRequestSent(false);
                  setEmail('');
                }}
                className="text-[var(--color-primary-400)] hover:underline text-sm"
              >
                Use a different email
              </button>
            </div>
          ) : (
            // Request form
            <form onSubmit={handleRequest} className="space-y-5">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center mx-auto mb-3">
                  <Wand2 className="w-6 h-6 text-[var(--color-primary-400)]" />
                </div>
                <p className="text-[var(--text-secondary)] text-sm">
                  Enter your email and we'll send you a magic link to sign in instantly, no password needed.
                </p>
              </div>

              {requestError && (
                <div className="p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)] text-sm">
                  {requestError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[var(--color-primary-400)] focus:ring-1 focus:ring-[var(--color-primary-400)] transition-colors"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={requestLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)] text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {requestLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Send Magic Link
                  </>
                )}
              </button>
            </form>
          )}

          {/* Back to login */}
          <div className="mt-6 pt-6 border-t border-[var(--bg-tertiary)] text-center">
            <Link to="/login" className="text-sm text-[var(--text-secondary)] hover:text-[var(--color-primary-400)]">
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

